import React from "react";
import dummy_user from "../../../Assets/images/user_square.png";
import { FaStar, FaRegStar } from "react-icons/fa";
import Tooltip from "../../Tooltip/Tooltip"; // Keep if you still use it
import Phone from "../../Phone/Phone";
import ApprovalStatus from "../../../Pages/ApproveUnapprove/ApprovalStatus";

const UserDetail = ({
  type, // "users" | "drivers" | "store"
  itemData,
  allApprovals,
  matchedData,
  waitingForApproval,
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Use real rating if available, fallback to 0
  const rating = Number(itemData?.averageRating || itemData?.rating || 0);
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-sm border border-gray-100 overflow-y-auto">
      {/* Header: Avatar + Name + Rating */}
      <div className="flex items-start gap-6 pb-8 border-b border-gray-100">
        <div className="relative flex-shrink-0">
          <img
            src={itemData?.profile_image || dummy_user}
            alt="Profile"
            className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md"
          />

          {/* Rating badge overlay */}
          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white rounded-full px-3 py-1 shadow-md flex items-center gap-1 text-sm font-medium">
            {[...Array(fullStars)].map((_, i) => (
              <FaStar key={`full-${i}`} className="text-amber-500" />
            ))}
            {hasHalf && (
              <div className="relative">
                <FaStar className="text-amber-500" style={{ clipPath: "inset(0 50% 0 0)" }} />
              </div>
            )}
            {[...Array(emptyStars)].map((_, i) => (
              <FaRegStar key={`empty-${i}`} className="text-gray-300" />
            ))}
            <span className="ml-1 text-gray-700">
              {rating ? rating.toFixed(1) : "—"}
            </span>
          </div>
        </div>

        <div className="flex-1 pt-2">
          <h2 className="text-2xl font-bold text-gray-800">
            {type === "store"
              ? itemData?.business_name || itemData?.first_name || "Store"
              : `${itemData?.first_name || ""} ${itemData?.last_name || ""}`.trim() || "User"}
          </h2>

          {type === "store" && itemData?.category && (
            <p className="text-gray-600 mt-1">{itemData.category}</p>
          )}

          <p className="text-sm text-gray-500 mt-2">
            Member since {formatDate(itemData?.createdAt)}
          </p>
        </div>
      </div>

      {/* Main Info Grid */}
      <div className="grid md:grid-cols-2 gap-6 mt-8">
        {/* Left column */}
        <div className="space-y-6">
          <InfoField
            label="Phone Number"
            value={itemData?.phone_number}
            component={<Phone disabled value={itemData?.phone_number} />}
          />

          <InfoField label="Email" value={itemData?.email || "—"} />

          {type === "store" && (
            <InfoField
              label="Full Address"
              value={itemData?.address || "Not provided"}
              multiline
            />
          )}

          {(type === "users" || type === "drivers") && (
            <>
              <InfoField label="First Name" value={itemData?.first_name || "—"} />
              <InfoField label="Last Name" value={itemData?.last_name || "—"} />
            </>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {type === "store" && (
            <div className="grid grid-cols-2 gap-6">
              <InfoField
                label="Admin Profit %"
                value="0%"
                bg="bg-gray-50"
              />
              <InfoField
                label="Vendor Profit %"
                value="0%"
                bg="bg-gray-50"
              />
            </div>
          )}

          <InfoField
            label="Registration Date"
            value={formatDate(itemData?.createdAt)}
          />

          {/* Add more fields here if needed (e.g., Wallet, Status) */}
        </div>
      </div>

      {/* Approval Section (only for stores) */}
      {type === "store" && (
        <div className="mt-10 pt-8 border-t border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Approval & Verification Status
          </h3>
          <ApprovalStatus
            matchedData={matchedData}
            waitingForApproval={waitingForApproval}
          />
        </div>
      )}
    </div>
  );
};

// Reusable Info Field Component
function InfoField({ label, value, component, multiline, bg = "" }) {
  return (
    <div className={`p-4 rounded-lg ${bg || "bg-gray-50/70"} border border-gray-100`}>
      <div className="flex items-center gap-2 mb-1.5">
        <label className="text-sm font-medium text-gray-600">{label}</label>
        {/* Uncomment if you want tooltips */}
        {/* <Tooltip text={`User's ${label.toLowerCase()}`} size={12} /> */}
      </div>

      {component ? (
        component
      ) : (
        <div
          className={`text-gray-900 font-medium ${
            multiline ? "whitespace-pre-wrap" : ""
          }`}
        >
          {value || "—"}
        </div>
      )}
    </div>
  );
}

export default UserDetail;